import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const requestUrl = error.config?.url || '';
            // Only treat as a session expiry if it came from a protected resource,
            // not if we're already on the login page or hitting an auth endpoint.
            const isAuthEndpoint = requestUrl.includes('/auth/');
            const alreadyOnLogin = window.location.pathname === '/login';
            if (!isAuthEndpoint && !alreadyOnLogin) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                // Use replace so the browser back button skips the broken page
                window.location.replace('/login');
            }
        }
        return Promise.reject(error);
    }
);

export const auth = {
    login: (username, password) =>
        api.post('/auth/login', { username, password }),
    register: (userData) =>
        api.post('/auth/register', userData),
    forgotPassword: (email) =>
        api.post('/auth/forgot-password', { email }),
    resetPassword: (token, password) =>
        api.post('/auth/reset-password', { token, password }),
};

export const users = {
    getAll: () => api.get('/users'),
    getMe: () => api.get('/users/me'),
    search: (q) => api.get('/users', { params: { q } }),
    update: (id, data) => api.put(`/users/${id}`, data),
};

export const events = {
    create: (eventData, timeSlots, organizerId) =>
        api.post('/events', {
            event: eventData,
            proposedTimeSlots: timeSlots,
            organizerId: organizerId
        }),
    getAll: () =>
        api.get('/events'),
    getById: (id) =>
        api.get(`/events/${id}`),
    update: (id, updateData) =>
        api.put(`/events/${id}`, updateData),
    delete: (id) =>
        api.delete(`/events/${id}`),
    getUpcoming: (userId) =>
        api.get(`/events/upcoming/${userId}`),
    getPendingVotes: (userId) =>
        api.get(`/events/pending-votes/${userId}`),
    addParticipant: (eventId, userId) =>
        api.post(`/events/${eventId}/participants/${userId}`),
    closeVoting: (eventId) =>
        api.post(`/events/${eventId}/close-voting`),
    selectTimeSlot: (eventId, timeSlotId) =>
        api.post(`/events/${eventId}/select-time-slot/${timeSlotId}`),
};

export const publicEvents = {
    getByToken: (token) => api.get(`/public/events/${token}`),
    castGuestVote: (token, timeSlotId, guestName, guestEmail, status) =>
        api.post(`/public/events/${token}/votes`, { timeSlotId, guestName, guestEmail, status }),
};

export const votes = {
    cast: (timeSlotId, userId, status) =>
        api.post(`/votes/time-slots/${timeSlotId}/users/${userId}`, null, { params: { status } }),
    getByTimeSlot: (timeSlotId) =>
        api.get(`/votes/time-slots/${timeSlotId}`),
    getStatistics: (timeSlotId) =>
        api.get(`/votes/time-slots/${timeSlotId}/statistics`),
    getBestTimeSlots: (eventId) =>
        api.get(`/votes/events/${eventId}/best-time-slots`),
};

export const subscriptions = {
    checkout: () => api.post('/subscriptions/checkout'),
    portal: () => api.post('/subscriptions/portal'),
};

export default api;
